import {tasksApiPathWithParams} from '@github-ui/agent-sessions/utils/api-url-helpers'
import {fetchCAPI} from '@github-ui/copilot-shared-ui/capi/fetch-capi'

/** Compact view of a Copilot cloud agent session (task), used by `list_sessions`. */
export interface AgentSession {
  taskId: string
  title: string
  state: string
  updatedAt: string
  /** Repository (owner/name) the session runs in, when known. */
  nwo?: string
}

interface RawAgentTask {
  id: string
  name?: string
  title?: string
  status?: string
  state?: string
  latest_session_state?: string
  nwo?: string
  prompt_preview?: string
  problem_statement?: string
  created_at?: string
  updated_at?: string
  last_updated_at?: string
}

interface RawAgentTasksResponse {
  tasks?: RawAgentTask[]
}

export interface ListAgentSessionsConfig {
  apiURL?: string
  signal?: AbortSignal
  limit?: number
}

/** Default number of cloud agent sessions to surface, to keep the model context small. */
const DEFAULT_AGENT_SESSIONS_LIMIT = 20

function transformAgentTask(raw: RawAgentTask): AgentSession {
  return {
    taskId: raw.id,
    title: raw.title || raw.name || raw.prompt_preview || raw.problem_statement || '',
    state: raw.latest_session_state ?? raw.state ?? raw.status ?? 'unknown',
    updatedAt: raw.updated_at ?? raw.last_updated_at ?? raw.created_at ?? '',
    ...(raw.nwo ? {nwo: raw.nwo} : {}),
  }
}

/**
 * List the user's Copilot cloud agent sessions (coding-agent tasks) via Mission
 * Control (browser side, with the user's credentials). This is the same
 * `/agents/tasks` listing the `/copilot/agents` dashboard uses — scoped to the
 * user by the token and NOT filtered to `type=chat`, so it returns cloud agent
 * sessions rather than quick-chat sessions. The `archived` filter is omitted so
 * both active and completed sessions are eligible (the dashboard's `all`
 * semantics). Every such task has an always-openable detail page, so there is no
 * "resumable" gate to apply.
 */
export async function listAgentSessions({
  apiURL,
  signal,
  limit = DEFAULT_AGENT_SESSIONS_LIMIT,
}: ListAgentSessionsConfig = {}): Promise<AgentSession[]> {
  const response = await fetchCAPI({
    path: tasksApiPathWithParams({page: 1, pageSize: limit}),
    copilotApiUrl: apiURL,
    additionalHeaders: {Accept: 'application/json'},
    signal,
  })
  const data = (await response.json()) as RawAgentTasksResponse
  return (data.tasks ?? []).map(transformAgentTask).slice(0, limit)
}
